/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useState } from "react";
import { type MatrixClient } from "matrix-js-sdk";
import { Root as Form, Button } from "@vector-im/compound-web";

import { FieldRow, InputField } from "../input/Input";
import { useSetting } from "./settings";
import {
  shareOnlineStatus,
  totpSecret,
  userStatus,
  userStatusMessage,
  type PresenceStatus,
} from "../meet/MeetSettings";
import { useSessionManager } from "../meet/features/security/useSessionManager";
import { useTotp } from "../meet/features/security/useTotp";
import styles from "./MeetSettings.module.css";

interface Props {
  client: MatrixClient;
}

export const AccountSettingsTab: FC<Props> = ({ client }) => {
  const [online, setOnline] = useSetting(shareOnlineStatus);
  const [secret, setSecret] = useSetting(totpSecret);
  const [status, setStatus] = useSetting(userStatus);
  const [statusMsg, setStatusMsg] = useSetting(userStatusMessage);

  useEffect(() => {
    if (!shareOnlineStatus) return;
    client
      .setPresence({ presence: status, status_msg: statusMsg || undefined })
      .catch(() => {
        /* presence is best-effort; ignore failures */
      });
  }, [client, status, statusMsg, shareOnlineStatus]);

  const { sessions, loading, revoke } = useSessionManager(client);
  const totp = useTotp(secret);
  const [codeInput, setCodeInput] = useState("");
  const [verifyMsg, setVerifyMsg] = useState("");

  return (
    <div>
      <h3 className={styles.sectionTitle}>Privacy</h3>
      <Form>
        <FieldRow>
          <InputField
            id="online"
            type="checkbox"
            label="Share last seen / online status"
            checked={online}
            onChange={(e) => setOnline(e.target.checked)}
          />
        </FieldRow>
      </Form>

      <h3 className={styles.sectionTitle}>Status & Presence</h3>
      <p className={styles.small}>
        Broadcast your availability to your Nevai homeserver. Requires
        &ldquo;Share last seen / online status&rdquo; above to be enabled.
      </p>
      <Form>
        <FieldRow>
          <label htmlFor="status">Availability</label>
          <select
            id="status"
            value={status}
            disabled={!online}
            onChange={(e) => setStatus(e.target.value as PresenceStatus)}
          >
            <option value="online">Online</option>
            <option value="unavailable">Away / Busy</option>
            <option value="offline">Invisible / Offline</option>
          </select>
        </FieldRow>
        <FieldRow>
          <InputField
            id="statusMsg"
            type="text"
            label="Status message"
            value={statusMsg}
            placeholder="e.g. In a meeting until 3pm"
            disabled={!online}
            onChange={(e) => setStatusMsg(e.target.value)}
          />
        </FieldRow>
        <Button
          disabled={!online}
          onClick={() =>
            client
              .setPresence({
                presence: status,
                status_msg: statusMsg || undefined,
              })
              .catch(() => {})
          }
        >
          Update status now
        </Button>
      </Form>

      <h3 className={styles.sectionTitle}>Local Two-Factor (TOTP)</h3>
      <p className={styles.small}>
        Generates time-based one-time passwords locally in your browser using
        Web Crypto. No SMS, no email, no external API.
      </p>
      {!secret ? (
        <Button onClick={() => setSecret(totp.generateSecret())}>
          Set up local 2FA
        </Button>
      ) : (
        <div>
          <div className={styles.totpBox}>{totp.code}</div>
          <p className={styles.small}>
            Refreshes in {totp.secondsLeft}s — add this secret to any authenticator
            app: <code>{secret}</code>
          </p>
          <FieldRow>
            <InputField
              id="totpVerify"
              type="text"
              label="Verify code"
              value={codeInput}
              placeholder="123456"
              onChange={(e) => setCodeInput(e.target.value)}
            />
          </FieldRow>
          <Button
            onClick={async () => {
              const ok = await totp.verify(codeInput);
              setVerifyMsg(ok ? "Code valid " : "Invalid code ");
            }}
          >
            Verify
          </Button>{" "}
          <Button onClick={() => setSecret("")}>Disable 2FA</Button>
          {verifyMsg && <p className={styles.small}>{verifyMsg}</p>}
        </div>
      )}

      <h3 className={styles.sectionTitle}>Sessions & Devices</h3>
      {loading ? (
        <p className={styles.small}>Loading devices…</p>
      ) : (
        sessions.map((s) => (
          <div key={s.deviceId} className={styles.session}>
            <div>
              <div>{s.displayName ?? s.deviceId}</div>
              <div className={styles.small}>
                {s.lastSeenIp ?? "unknown IP"} ·{" "}
                {s.lastSeenTs
                  ? new Date(s.lastSeenTs).toLocaleString()
                  : "never"}{" "}
                {s.isCurrent ? "· (this device)" : ""}
              </div>
            </div>
            {!s.isCurrent && (
              <Button onClick={() => void revoke(s.deviceId)}>Revoke</Button>
            )}
          </div>
        ))
      )}
    </div>
  );
};
